import {
  type LoadableState,
  useEffectWithController
} from '@carvana/merch-react-hooks';
import { useRef } from 'react';
import type {
  ConfigurationRequest,
  ResponseForRequest
} from '~/contracts/new-cars';
import fetchNewCarConfiguration from '~/services/new-cars/fetchNewCarConfiguration';
import { generateErrorLogger } from '~/utilities/loggers';
import {
  type ConfigurationSortKey,
  sortConfigurationResults
} from '~/utilities/new-cars/sortConfigurationResults';

type OwnArgs<R extends ConfigurationRequest> = {
  loadableState: LoadableState<ResponseForRequest<R>>;
  request: R | null;
  sortKey?: ConfigurationSortKey;
};

export default function useGetNewCarConfiguration<
  R extends ConfigurationRequest
>({ loadableState, request, sortKey = 'lowestPrice' }: OwnArgs<R>) {
  const isInitialRender = useRef(true);
  // Captures isLoaded at mount time. NOT in the dep array — including
  // loadableState.isLoaded caused an infinite abort/refetch cycle because
  // setLoading() sets isLoaded = false, re-triggering the effect.
  const initialIsLoadedRef = useRef(loadableState.isLoaded);

  const { setData, setLoading, setError } = loadableState;

  useEffectWithController(
    controller => {
      if (isInitialRender.current) {
        isInitialRender.current = false;
        // Fall through when request is present but data isn't loaded yet
        // (restoration without server-prefetch). Skip when data is already loaded.
        if (!request || initialIsLoadedRef.current) return;
      }

      if (!request) return;

      setLoading();

      fetchNewCarConfiguration(request, controller)
        .then(data => ({
          ...data,
          results: sortConfigurationResults(data.results, sortKey)
        }))
        .then(setData)
        .catch(
          generateErrorLogger(
            'Get New Car Configuration',
            { request },
            { callback: setError, controller }
          )
        );
    },
    [request, setData, setError, setLoading, sortKey]
  );
}
